import { useCallback, useEffect, useState } from "react";
import "./paneles.css";
import { CampanasYRespuestas, GestorDeMensajes, NegociosContactados, NuevaCampana } from "./LeadslinkCampanas";

/**
 * El panel de Leadslink: los ojos de Jorge sobre la captación.
 *
 * ── Qué manda en el orden de la pantalla ────────────────────────────────
 *
 * Lo que está ROTO va antes que lo que está bien, y lo que espera a una
 * persona antes que lo que espera a una máquina. Por eso el orden es:
 * enviador, leads sin contestar, campañas en marcha, y el resto.
 *
 * No es una preferencia estética. El 26/08/2026 el enviador estuvo desde las
 * 07:37 pidiendo un QR que nadie escaneó, con la captación entera parada y sin
 * que nada lo dijera. Un panel donde eso quede por debajo del embudo es un
 * panel que no sirve para el único día en que hace falta.
 *
 * ── Y por qué el QR está aquí dentro ────────────────────────────────────
 *
 * Porque el enviador escucha en el 127.0.0.1 del servidor y no se le llega
 * desde fuera: «abre /qr y escanéalo» era una instrucción imposible de
 * ejecutar. BilboBot sí le llega, así que lo sirve él. Se escanea de la
 * pantalla con el móvil y la captación arranca.
 */

interface Estado {
  listo: boolean | null;
  estado: string | null;
  enHorario: boolean | null;
  esperandoQr: boolean | null;
  enviadosHoy: number | null;
  enCola: number | null;
  objetivoDiario: number | null;
  franjas: string[];
  horaEs: string | null;
}

interface Datos {
  configurado: boolean;
  porQue?: string;
  estado: Estado | null;
  cupo: { cupo: number; espera: string | null; porQue: string };
  embudo: {
    contactados: number;
    respondidos: number;
    clientes: number;
    ingreso_mensual_recurrente: number;
  } | null;
  pendientes: {
    count: number;
    pending: {
      phone: string;
      name?: string | null;
      lastReply?: string | null;
      waLink?: string | null;
    }[];
  } | null;
  clientes: {
    total: number;
    ingresoMensual: number;
    clientes: {
      name: string;
      monthly_cost?: number | null;
      service_type?: string | null;
    }[];
  } | null;
  rendimiento: {
    tandas: number;
    seleccionados: number;
    conWhatsapp: number;
    acierto: number;
  };
  nuestro: {
    hoy: number;
    tandas: {
      cuando: string;
      resumen: string;
      sector: string | null;
      pedidos: number | null;
      salieron: number | null;
      pendientes: number | null;
      espera: string | null;
      por_que: string | null;
    }[];
  };
  esperando: {
    id: string;
    titulo: string;
    queVaAPasar: string;
    porQue: string;
    explicacionDelRiesgo: string;
    comoSeVuelveAtras: string | null;
  }[];
}

/**
 * Las pestañas del panel (23/10/2026). Jorge: «organizarlo mejor para poder
 * controlar todo desde ahí». Arriba, siempre, las cifras que importan; debajo,
 * una cosa cada vez.
 */
type Pestana = "resumen" | "nueva" | "campanas" | "negocios" | "mensajes" | "enviador";
const PESTANAS: { id: Pestana; nombre: string }[] = [
  { id: "resumen", nombre: "Resumen" },
  { id: "nueva", nombre: "Nueva campaña" },
  { id: "campanas", nombre: "Campañas" },
  { id: "negocios", nombre: "Negocios" },
  { id: "mensajes", nombre: "Mensajes" },
  { id: "enviador", nombre: "Enviador" },
];

function pestanaGuardada(): Pestana {
  try {
    const p = localStorage.getItem("leadslink.pestana");
    return PESTANAS.some((x) => x.id === p) ? (p as Pestana) : "resumen";
  } catch {
    return "resumen";
  }
}

/** Cada cuánto se vuelve a mirar. Lo mismo que hace «Hoy». */
const CADA = 5_000;

export function LeadslinkPanel() {
  const [datos, setDatos] = useState<Datos | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* Para forzar un QR nuevo: el suyo caduca cada veinte segundos. */
  const [qrDe, setQrDe] = useState(0);
  const [pestana, setPestanaYa] = useState<Pestana>(pestanaGuardada);
  const setPestana = (p: Pestana) => {
    setPestanaYa(p);
    try {
      localStorage.setItem("leadslink.pestana", p);
    } catch {
      /* Sin almacenamiento, se queda en la de ahora. */
    }
  };

  const cargar = useCallback(async () => {
    try {
      const r = await fetch("/api/leadslink", { credentials: "same-origin" });
      if (!r.ok) throw new Error(`El servidor contestó ${r.status}`);
      setDatos((await r.json()) as Datos);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), CADA);
    return () => clearInterval(t);
  }, [cargar]);

  /*
   * El QR se refresca cada quince segundos mientras haga falta, y sólo
   * entonces. Pedirlo siempre sería sacarle una foto a un Chromium que puede
   * estar arrancando, cada quince segundos, para nada.
   */
  const hazFaltaQr = datos?.estado?.listo !== true && datos?.configurado === true;
  useEffect(() => {
    if (!hazFaltaQr) return;
    const t = setInterval(() => setQrDe((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, [hazFaltaQr]);

  if (error && !datos)
    return (
      <main className="leadslink pnl-leadslink">
        <p className="vacio">No se pudo leer el panel: {error}</p>
      </main>
    );

  if (!datos) return <main className="leadslink pnl-leadslink"><p className="vacio">Mirando…</p></main>;

  if (!datos.configurado)
    return (
      <main className="leadslink pnl-leadslink">
        <p className="vacio">{datos.porQue}</p>
      </main>
    );

  const { estado, cupo, embudo, pendientes, clientes, rendimiento, nuestro, esperando } =
    datos;

  const parado = estado?.listo !== true;
  const objetivo = estado?.objetivoDiario ?? null;
  const porContestar = pendientes?.count ?? 0;
  const insignia: Partial<Record<Pestana, number>> = {
    campanas: esperando.length,
    negocios: porContestar,
  };

  return (
    <main className="leadslink pnl-leadslink">
      <header className="pnl-cabecera ll-cabecera">
        <div>
          <h1>Leadslink</h1>
          <p className="pnl-sub">
            La captación por WhatsApp. Buscas, ves cada negocio con su mensaje y apruebas: nada sale sin tu visto bueno.
          </p>
        </div>
        <button type="button" className="principal" onClick={() => setPestana("nueva")}>
          + Nueva campaña
        </button>
      </header>

      <div className="ll-kpis">
        <button type="button" className={`ll-kpi ${parado ? "es-mal" : "es-ok"}`} onClick={() => setPestana("enviador")}>
          <span className="ll-kpi-etiqueta">Enviador</span>
          <strong>{!estado ? "No contesta" : parado ? "Sin sesión" : estado.enHorario ? "Mandando" : "Conectado"}</strong>
          <span className="ll-kpi-pie">
            {estado && !parado && !estado.enHorario ? `franjas ${estado.franjas.join(" y ")}` : "\u00a0"}
          </span>
        </button>
        <button type="button" className="ll-kpi" onClick={() => setPestana("enviador")}>
          <span className="ll-kpi-etiqueta">Enviados hoy</span>
          <strong>
            {estado?.enviadosHoy ?? "—"}
            {objetivo !== null && <span className="ll-kpi-de"> / {objetivo}</span>}
          </strong>
          <span className="ll-kpi-pie">{(estado?.enCola ?? 0) > 0 ? `${estado?.enCola} en cola` : "\u00a0"}</span>
        </button>
        <button type="button" className={`ll-kpi ${esperando.length ? "es-aviso" : ""}`} onClick={() => setPestana("campanas")}>
          <span className="ll-kpi-etiqueta">Por aprobar</span>
          <strong>{esperando.length}</strong>
          <span className="ll-kpi-pie">{esperando.length ? "esperan tu visto bueno" : "\u00a0"}</span>
        </button>
        <button type="button" className={`ll-kpi ${porContestar ? "es-aviso" : ""}`} onClick={() => setPestana("negocios")}>
          <span className="ll-kpi-etiqueta">Te han escrito</span>
          <strong>{pendientes ? porContestar : "—"}</strong>
          <span className="ll-kpi-pie">{porContestar ? "sin contestar" : "\u00a0"}</span>
        </button>
        <button type="button" className="ll-kpi" onClick={() => setPestana("resumen")}>
          <span className="ll-kpi-etiqueta">Clientes</span>
          <strong>{embudo?.clientes ?? "—"}</strong>
          <span className="ll-kpi-pie">{embudo ? `${embudo.respondidos} respondieron` : "\u00a0"}</span>
        </button>
      </div>

      <nav className="ll-tabs" role="tablist" aria-label="Secciones de Leadslink">
        {PESTANAS.map((p) => (
          <button
            type="button"
            role="tab"
            key={p.id}
            aria-selected={pestana === p.id}
            className={pestana === p.id ? "es-activo" : ""}
            onClick={() => setPestana(p.id)}
          >
            {p.nombre}
            {(insignia[p.id] ?? 0) > 0 && <span className="ll-insignia">{insignia[p.id]}</span>}
            {p.id === "enviador" && parado && <span className="ll-insignia es-mal">!</span>}
          </button>
        ))}
      </nav>

      {/* Con el enviador parado no sale nada: se dice en todas las pestañas. */}
      {parado && pestana !== "enviador" && (
        <SeccionEnviador estado={estado} cupo={cupo} qrDe={qrDe} nuestro={nuestro.hoy} />
      )}

      {pestana === "resumen" && (
        <>
          <SeccionEsperando esperando={esperando} recargar={cargar} />
          <SeccionPendientes pendientes={pendientes} />
          <SeccionEmbudo embudo={embudo} clientes={clientes} rendimiento={rendimiento} />
          <CampanasYRespuestas solo="campanas" />
        </>
      )}
      {pestana === "nueva" && (
        <NuevaCampana
          puedeMandar={!parado}
          alPedir={async () => {
            await cargar();
            setPestana("campanas");
          }}
        />
      )}
      {pestana === "campanas" && (
        <>
          <SeccionEsperando esperando={esperando} recargar={cargar} />
          <CampanasYRespuestas solo="campanas" />
          <SeccionCampanas tandas={nuestro.tandas} />
        </>
      )}
      {pestana === "negocios" && (
        <>
          <SeccionPendientes pendientes={pendientes} />
          <NegociosContactados />
        </>
      )}
      {pestana === "mensajes" && <GestorDeMensajes />}
      {pestana === "enviador" && (
        <SeccionEnviador estado={estado} cupo={cupo} qrDe={qrDe} nuestro={nuestro.hoy} />
      )}
    </main>
  );
}

/**
 * Las campañas esperando el visto bueno, con sus dos botones.
 *
 * Se deciden con la MISMA ruta que usa «Hoy» y que usa el chat. Tres sitios
 * donde se ve, un solo sitio donde se decide: si cada pantalla tuviera su
 * camino, aprobar desde aquí y aprobar desde allí acabarían haciendo cosas
 * distintas.
 */
function SeccionEsperando({
  esperando,
  recargar,
}: {
  esperando: Datos["esperando"];
  recargar: () => Promise<void>;
}) {
  const [yendo, setYendo] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  if (esperando.length === 0) return null;

  async function decidir(id: string, aprobada: boolean) {
    setYendo(id);
    setFallo(null);
    try {
      const r = await fetch(`/api/hoy/aprobar/${id}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ aprobada }),
      });
      if (!r.ok) {
        const c = (await r.json()) as { error?: string };
        setFallo(c.error ?? `El servidor contestó ${r.status}`);
        return;
      }
      await recargar();
    } catch (e) {
      setFallo(String(e));
    } finally {
      setYendo(null);
    }
  }

  return (
    <section className="bloque urgente">
      <h2>Esperan tu visto bueno ({esperando.length})</h2>
      {esperando.map((a) => (
        <div className="espera" key={a.id}>
          <div className="titulo">{a.titulo}</div>
          {/* El plan tal cual, con sus saltos de línea: es lo que se aprueba. */}
          <pre className="plan">{a.queVaAPasar}</pre>
          <p className="pie">
            <strong>Por qué:</strong> {a.porQue}
          </p>
          <p className="aviso">{a.explicacionDelRiesgo}</p>
          {a.comoSeVuelveAtras && (
            <p className="pie">
              <strong>Si te arrepientes:</strong> {a.comoSeVuelveAtras}
            </p>
          )}
          <div className="botones">
            <button
              type="button"
              className="principal"
              disabled={yendo === a.id}
              onClick={() => void decidir(a.id, true)}
            >
              {yendo === a.id ? "…" : "Aprobar y empezar"}
            </button>
            <button
              type="button"
              disabled={yendo === a.id}
              onClick={() => void decidir(a.id, false)}
            >
              No hacerlo
            </button>
          </div>
        </div>
      ))}
      {fallo && <p className="aviso">{fallo}</p>}
    </section>
  );
}

/**
 * El enviador, arriba del todo y grande.
 *
 * Es lo único de esta pantalla que puede estar roto ahora mismo, y cuando lo
 * está no hay captación de ninguna clase. Con sesión ocupa tres líneas; sin
 * ella ocupa media pantalla y lleva el QR dentro.
 */
function SeccionEnviador({
  estado,
  cupo,
  qrDe,
  nuestro,
}: {
  estado: Estado | null;
  cupo: Datos["cupo"];
  qrDe: number;
  nuestro: number;
}) {
  if (!estado)
    return (
      <section className="bloque roto">
        <h2>El enviador</h2>
        <p className="grande">Leadslink no contesta.</p>
        <p className="pie">
          No se sabe si puede mandar, así que no se le pide nada. Se vuelve a
          mirar solo.
        </p>
      </section>
    );

  if (estado.listo !== true)
    return (
      <section className="bloque roto">
        <h2>El enviador está parado</h2>
        <p className="grande">
          {estado.esperandoQr === true
            ? "Escanea este código con el móvil dedicado"
            : `No tiene sesión de WhatsApp (${estado.estado ?? "estado desconocido"})`}
        </p>
        {estado.esperandoQr === true && (
          <>
            <img
              className="qr"
              /* La clave del final fuerza una imagen nueva: cada QR caduca en 20 s. */
              src={`/api/leadslink/qr?t=${qrDe}`}
              alt="Código QR para enlazar el WhatsApp del enviador"
              width={280}
              height={280}
            />
            <p className="pie">
              En el móvil: <strong>WhatsApp → Ajustes → Dispositivos vinculados →
              Vincular un dispositivo</strong>. El código se renueva solo cada pocos
              segundos; si falla, espera a que cambie y vuelve a probar.
            </p>
          </>
        )}
        <p className="aviso">
          Mientras siga así no sale ni un mensaje. Y no se le pide nada a
          propósito: Leadslink apunta a los negocios como contactados{" "}
          <strong>antes</strong> de mandarles el mensaje, así que pedir ahora
          quemaría la lista sin que llegara nada.
        </p>
      </section>
    );

  const objetivo = estado.objetivoDiario;
  const enviados = estado.enviadosHoy ?? 0;
  const porcentaje = objetivo ? Math.min(100, Math.round((enviados / objetivo) * 100)) : 0;

  return (
    <section className="bloque">
      <h2>El enviador</h2>
      <div className="barra" title={`${enviados} de ${objetivo ?? "?"}`}>
        <div className="relleno" style={{ width: `${porcentaje}%` }} />
      </div>
      <p className="grande">
        {enviados}
        {objetivo !== null && <span className="de"> / {objetivo}</span>} enviados hoy
      </p>
      <ul className="datos">
        <li>
          {estado.enHorario === true
            ? "Mandando ahora"
            : `En pausa hasta su franja (${estado.franjas.join(" y ") || "sin franjas"})`}
        </li>
        {(estado.enCola ?? 0) > 0 && <li>{estado.enCola} esperando turno</li>}
        {nuestro > 0 && <li>Le hemos pedido {nuestro} hoy</li>}
      </ul>
      <p className="pie">{cupo.porQue}</p>
    </section>
  );
}

/**
 * Los que han escrito y esperan respuesta.
 *
 * Va segundo porque es lo único de la pantalla que espera a una PERSONA. Los
 * contesta Jorge desde WhatsApp: BilboBot no escribe a nadie, y por eso aquí
 * hay un enlace y no un botón de responder.
 */
function SeccionPendientes({ pendientes }: { pendientes: Datos["pendientes"] }) {
  if (!pendientes)
    return (
      <section className="bloque">
        <h2>Te han escrito</h2>
        <p className="pie">No se pudo preguntar a Leadslink.</p>
      </section>
    );

  if (pendientes.count === 0)
    return (
      <section className="bloque apagado">
        <h2>Te han escrito</h2>
        <p className="pie">Nadie espera respuesta ahora mismo.</p>
      </section>
    );

  return (
    <section className="bloque urgente">
      <h2>Te han escrito ({pendientes.count})</h2>
      <ul className="leads">
        {pendientes.pending.map((l) => (
          <li key={l.phone}>
            <div className="quien">{(l.name ?? "").trim() || l.phone}</div>
            {l.lastReply && <div className="dijo">«{l.lastReply}»</div>}
            {l.waLink && (
              <a className="boton" href={l.waLink} target="_blank" rel="noreferrer">
                Abrir el chat
              </a>
            )}
          </li>
        ))}
      </ul>
      <p className="pie">Los contestas tú desde WhatsApp. BilboBot no escribe a nadie.</p>
    </section>
  );
}

/** Qué se ha pedido y cuándo, para ver si una campaña está avanzando o atascada. */
function SeccionCampanas({ tandas }: { tandas: Datos["nuestro"]["tandas"] }) {
  if (tandas.length === 0) return null;

  return (
    <details className="bloque ll-historial">
      <summary>Historial de tandas ({tandas.length})</summary>
      <ul className="tandas">
        {tandas.map((t, i) => (
          <li key={`${t.cuando}-${i}`} className={t.espera ? "parada" : ""}>
            <span className="cuando">
              {new Date(t.cuando).toLocaleString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            <span className="que">{t.resumen}</span>
            {/*
              Lo que salió es un SUELO, no un exacto: se mide por la diferencia
              de lo enviado más lo encolado, y los descartados por no tener
              WhatsApp la bajan. Se dice «al menos» para no dar por exacta una
              cifra que se equivoca hacia abajo a propósito.
            */}
            {t.salieron !== null && t.salieron > 0 && (
              <span className="salieron">salieron al menos {t.salieron}</span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** El embudo, y los números suyos que no se pueden dar por buenos. */
function SeccionEmbudo({
  embudo,
  clientes,
  rendimiento,
}: {
  embudo: Datos["embudo"];
  clientes: Datos["clientes"];
  rendimiento: Datos["rendimiento"];
}) {
  /*
   * Su recurrente contra la suma de sus propios clientes.
   *
   * Dice 0 € teniendo catorce clientes que suman más de 4.500, porque sólo
   * cuenta los que tienen un `service_type` que empiece por «mensual» y los
   * suyos ponen «seo_web». Repetir ese cero sería dar por dato un fallo de
   * mapeo.
   */
  const suma = (clientes?.clientes ?? []).reduce(
    (n, c) => n + (c.monthly_cost ?? 0),
    0,
  );
  const noCuadra = clientes !== null && suma > 0 && clientes.ingresoMensual === 0;

  return (
    <section className="bloque">
      <h2>Cómo va</h2>
      {embudo ? (
        <ul className="datos">
          <li>
            <strong>{embudo.contactados}</strong> en conversación
          </li>
          <li>
            <strong>{embudo.respondidos}</strong> respondieron
          </li>
          <li>
            <strong>{embudo.clientes}</strong> ya son clientes
          </li>
        </ul>
      ) : (
        <p className="pie">No se pudo leer el embudo.</p>
      )}

      {noCuadra && (
        <p className="aviso">
          Leadslink dice <strong>0 €</strong> de recurrente, pero sus{" "}
          {clientes.total} clientes suman {suma.toFixed(2)} €. Es su tipo de
          servicio: no reconoce el que tienen puesto. Se arregla volviendo a
          sincronizarlos.
        </p>
      )}

      <p className="pie">
        {rendimiento.tandas > 0 ? (
          <>
            De cada 10 negocios que se piden, <strong>
              {Math.round(rendimiento.acierto * 10)}
            </strong>{" "}
            tienen WhatsApp — medido en {rendimiento.tandas} tanda(s),{" "}
            {rendimiento.conWhatsapp} de {rendimiento.seleccionados}.
          </>
        ) : (
          "Todavía no hay ninguna tanda medida, así que no se sabe qué parte de los que se piden tendrá WhatsApp."
        )}
      </p>
    </section>
  );
}
